import { useMemo, useState } from 'react'
import { KeyRound, ShieldCheck, UserPlus } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { apiClient, toApiProblem } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'
import { useQuery } from '@tanstack/react-query'
import { useAdminUsers, useSysAdminMutations } from '../api'
import type { InvitationView } from '../types'
import { InvitationLink } from '../components/InvitationLink'
import { UserRolesDialog } from '../components/UserRolesDialog'
import { usePermission } from '@/hooks/usePermission'
import { inviteLinkFor } from '../components/inviteLink'

interface RoleOption { id: string; code: string; name: string }

/**
 * Accounts.
 *
 * <p>Nothing here deletes a user. Disabling keeps every approval, audit row and login that
 * points at them resolving to a name; deleting turns a year of history into identifiers.
 */
export function UsersPage() {
  useBreadcrumb()
  const users = useAdminUsers()
  const actions = useSysAdminMutations()
  const roles = useQuery({
    queryKey: ['administration', 'role-options'],
    queryFn: async () => (await apiClient.get<RoleOption[]>('/v1/admin/roles')).data,
  })

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [invite, setInvite] = useState<{
    email: string; fullName: string; employeeId: string; roleId: string
  } | null>(null)
  const [issued, setIssued] = useState<InvitationView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<string | null>(null)
  const canGrant = usePermission('admin:grants')
  const [rolesFor, setRolesFor] = useState<{ id: string; email: string; fullName: string } | null>(null)

  /**
   * The email and name from the employee's record, so they are not typed twice and typed
   * differently. The work email first — it is what they sign in with — then the personal one.
   */
  async function fillFromEmployee(employeeId: string) {
    try {
      const detail = (await apiClient.get<{
        summary: { displayName: string; workEmail: string | null }
        personalEmail: string | null
      }>(`/v1/employees/${employeeId}`)).data
      setInvite((current) => current && current.employeeId === employeeId
        ? {
            ...current,
            email: detail.summary.workEmail ?? detail.personalEmail ?? current.email,
            fullName: detail.summary.displayName ?? current.fullName,
          }
        : current)
    } catch {
      // Not being able to prefill is not a reason to stop somebody typing the two fields.
    }
  }

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (users.data ?? []).filter((user) => {
      if (status && user.status !== status) return false
      if (!term) return true
      return user.email.toLowerCase().includes(term)
        || user.fullName.toLowerCase().includes(term)
        || (user.employeeName ?? '').toLowerCase().includes(term)
    })
  }, [users.data, search, status])

  const run = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setBanner(null)
      try {
        await work()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        subtitle="Who can sign in, what they hold, and how to stop them."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setInvite({ email: '', fullName: '', employeeId: '', roleId: '' })
            }}
          >
            <UserPlus className="size-4" aria-hidden="true" />
            Invite
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <FilterBar
        search={
          <Input
            aria-label="Search accounts"
            placeholder="Search by name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        }
      >
        <Select aria-label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">Any status</option>
          <option value="ACTIVE">Active</option>
          <option value="INVITED">Invited</option>
          <option value="DISABLED">Disabled</option>
        </Select>
      </FilterBar>

      <Card>
        {users.isPending && <Skeleton className="m-4 h-24" />}
        {users.isError && <ErrorState error={users.error} onRetry={() => void users.refetch()} />}
        {users.isSuccess && rows.length === 0 && (
          <EmptyState
            title="No account matches"
            description="Change the filter, or invite somebody."
          />
        )}
        {users.isSuccess && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Account</th>
                  <th className="p-3 text-start font-medium">Employee</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-start font-medium">Second factor</th>
                  <th className="p-3 text-start font-medium">Roles</th>
                  <th className="p-3 text-start font-medium">Sessions</th>
                  <th className="p-3 text-start font-medium">Last seen</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id} className="border-b border-[var(--color-border)] align-top">
                    <td className="p-3">
                      <p className="font-medium text-[var(--color-heading)]">{user.fullName}</p>
                      <p className="text-xs text-[var(--color-muted)]">{user.email}</p>
                      {user.invitationNote && (
                        <p className="mt-1 text-xs text-[var(--color-warning)]">{user.invitationNote}</p>
                      )}
                    </td>
                    <td className="p-3">{user.employeeName ?? '—'}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={user.status}
                        tone={statusTone(user.status)}
                        label={statusLabel(user.status)}
                      />
                      {user.lockedUntil && (
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          Locked until <DateValue value={user.lockedUntil} withTime />
                        </p>
                      )}
                    </td>
                    <td className="p-3">
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          className="size-4"
                          aria-label={`Require a second factor for ${user.email}`}
                          checked={user.mfaEnabled}
                          onChange={(event) =>
                            run(
                              () => actions.setMfa.mutateAsync({
                                userId: user.id, enabled: event.target.checked,
                              }),
                              'That could not be changed',
                            )}
                        />
                        {user.mfaEnabled ? 'Required' : 'Off'}
                      </label>
                    </td>
                    <td className="p-3 text-sm">
                      {user.roles.length > 0
                        ? user.roles.join(', ')
                        : <span className="text-[var(--color-muted)]">None</span>}
                    </td>
                    <td className="p-3">{user.liveSessions}</td>
                    <td className="p-3">
                      {user.lastLoginAt
                        ? <DateValue value={user.lastLoginAt} withTime />
                        : <span className="text-[var(--color-muted)]">Never</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {canGrant && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => setRolesFor({ id: user.id, email: user.email, fullName: user.fullName })}
                          >
                            <ShieldCheck className="size-4" aria-hidden="true" />
                            Roles
                          </Button>
                        )}
                        {user.status === 'INVITED' && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() =>
                              run(async () => setIssued(await actions.reinvite.mutateAsync(user.id)),
                                'That invitation could not be reissued')}
                          >
                            <KeyRound className="size-4" aria-hidden="true" />
                            Re-invite
                          </Button>
                        )}
                        {user.liveSessions > 0 && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() =>
                              run(() => actions.forceLogout.mutateAsync({ userId: user.id }),
                                'Those sessions could not be ended')}
                          >
                            Sign out
                          </Button>
                        )}
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() =>
                            run(
                              () => actions.setStatus.mutateAsync({
                                userId: user.id,
                                status: user.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED',
                              }),
                              'That account could not be changed',
                            )}
                        >
                          {user.status === 'DISABLED' ? 'Enable' : 'Disable'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        An account is never deleted here. Disabling ends every live session at once and keeps
        the audit trail, approvals and login history resolving to a name.
      </p>

      <UserRolesDialog
        user={rolesFor}
        open={rolesFor !== null}
        onOpenChange={(next) => { if (!next) setRolesFor(null) }}
      />

      <FormDialog
        open={invite != null}
        onOpenChange={(open) => { if (!open) { setInvite(null); setError(null); setFieldErrors({}) } }}
        title="Invite somebody"
        submitLabel="Send invitation"
        loading={actions.invite.isPending}
        error={error}
        onSubmit={() => {
          if (!invite) return
          void (async () => {
            setError(null)
            setFieldErrors({})
            try {
              const created = await actions.invite.mutateAsync({
                email: invite.email,
                fullName: invite.fullName,
                employeeId: invite.employeeId || null,
                roleId: invite.roleId || null,
              })
              setInvite(null)
              setIssued(created)
            } catch (caught) {
              const problem = formProblem(caught, 'That invitation could not be created')
              setFieldErrors(problem.fields)
              setError(problem.summary)
            }
          })()
        }}
      >
        {invite && (
          <div className="grid gap-3">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email" type="email" value={invite.email}
                invalid={Boolean(fieldErrors.email)}
                onChange={(event) => setInvite({ ...invite, email: event.target.value })}
              />
              {fieldErrors.email && (
                <p role="alert" className="mt-1 text-xs text-[var(--color-danger)]">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="invite-name">Full name</Label>
              <Input
                id="invite-name" value={invite.fullName}
                invalid={Boolean(fieldErrors.fullName)}
                onChange={(event) => setInvite({ ...invite, fullName: event.target.value })}
              />
              {fieldErrors.fullName && (
                <p role="alert" className="mt-1 text-xs text-[var(--color-danger)]">{fieldErrors.fullName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="invite-employee">Employee</Label>
              <EmployeePicker
                  id="invite-employee"
                  value={invite.employeeId}
                  onChange={(employeeId) => {
                    setInvite({ ...invite, employeeId: employeeId ?? '' })
                    if (employeeId) void fillFromEmployee(employeeId)
                  }}
                />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Choosing somebody fills in their email and name from their record. Both stay
                editable.
              </p>
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select
                id="invite-role" value={invite.roleId}
                onChange={(event) => setInvite({ ...invite, roleId: event.target.value })}
              >
                <option value="">No role yet</option>
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                A role can be granted afterwards on the grants screen, with the entities it
                applies to.
              </p>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={issued != null}
        onOpenChange={(open) => { if (!open) setIssued(null) }}
        title="Invitation created"
        submitLabel="Done"
        onSubmit={() => setIssued(null)}
      >
        {issued && (
          <div className="space-y-3 text-sm">
            <p>
              The invitation for <span className="font-medium">{issued.sentTo}</span> expires{' '}
              <DateValue value={issued.expiresAt} withTime />.
            </p>
            {inviteLinkFor(issued.token, issued.acceptUrl) && (
              <InvitationLink
                link={inviteLinkFor(issued.token, issued.acceptUrl)!}
                sentTo={issued.sentTo}
                emailed={issued.emailed}
              />
            )}
            <p className="text-[var(--color-muted)]">{issued.note}</p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}

function statusTone(status: string) {
  if (status === 'ACTIVE') return 'success' as const
  if (status === 'INVITED') return 'warning' as const
  return 'neutral' as const
}

function statusLabel(status: string) {
  if (status === 'ACTIVE') return 'Active'
  if (status === 'INVITED') return 'Invited'
  return 'Disabled'
}
